import { useGlobalTapPaymentDidLoad, usePersistedCountryDetails, useScript } from 'common/hooks';
import React, { useEffect, useRef, useState } from 'react';
import { useStorePaymentDetails } from 'service/hooks';
import config from 'common/config';
import { useTheme } from '@zydalabs/storefront-components';
import { getLanguageDirection } from 'common/utils';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { StyledErrorWrapper, StyledTapWrapper } from './style';

declare const Tapjsli: any;

const TapPaymentCard: React.FC<any> = ({ setPaymentCard, setIsSavingCard }) => {
  const theme: any = useTheme();
  const { t } = useTranslation('payment');
  const router = useRouter();

  const { data: paymentData } = useStorePaymentDetails();
  const [persistedCountryDetails] = usePersistedCountryDetails();
  const { currency } = persistedCountryDetails || {};
  const [tapPaymentDidLoad, setTapPaymentDidLoad] = useGlobalTapPaymentDidLoad();

  const [errorMessage, setErrorMessage] = useState();
  const [isRendered, setIsRendered] = useState(false);
  const [blueBirdScriptLoad, setBlueBirdScriptLoad] = useState(false);
  const [tapScriptLoad, setTapScriptLoad] = useState(false);
  useScript({ url: config.common.tapBlueBirdScriptUrl, setLoaded: setBlueBirdScriptLoad });
  useScript({ url: config.common.tapScriptUrl, setLoaded: setTapScriptLoad });

  const tapFormContainer = useRef(null);
  const isMountedRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const languageDirection = getLanguageDirection(router.locale);

  useEffect(() => {
    let submitCard;
    const tapForm = tapFormContainer.current;

    if (blueBirdScriptLoad && tapScriptLoad && paymentData && !isRendered && currency) {
      const tap = Tapjsli(paymentData.paymentPublicApiKey);
      const tapElements = tap.elements({});

      const style = {
        base: {
          color: theme.color.headlineText,
          lineHeight: '22px',
          fontFamily: 'sans-serif',
          fontSmoothing: 'antialiased',
          fontSize: '15px',
          '::placeholder': {
            color: 'rgba(0, 0, 0, 0.26)',
            fontSize: '15px',
          },
        },
        invalid: {
          color: theme.color.danger,
        },
      };

      const labels = {
        cardNumber: t('cardNumber'),
        expirationDate: t('expirationDatePlaceholder'),
        cvv: t('cvv'),
        cardHolder: t('cardHolder'),
      };

      const paymentOptions = {
        currencyCode: [currency?.title_en],
        labels,
        TextDirection: languageDirection,
      };

      const tapCardElement = tapElements.create('card', { style }, paymentOptions);
      tapCardElement.mount('#tap-card-container');

      const changeHandler = event => {
        if (isMountedRef.current) {
          if (event.error) {
            setErrorMessage(event?.error?.message);
          } else {
            setErrorMessage(null);
          }
        }
      };

      submitCard = async event => {
        if (isMountedRef.current) {
          setIsSavingCard(true);
          event.preventDefault();
          await tap.createToken(tapCardElement).then(result => {
            if (result.error) {
              setErrorMessage(result.error.message);
            } else {
              setPaymentCard(result);
            }
            setIsSavingCard(false);
          });
        }
      };
      tapForm.addEventListener('submit', submitCard);

      if (!tapPaymentDidLoad && isMountedRef.current) {
        tapCardElement.addEventListener('change', changeHandler);
        setTapPaymentDidLoad(true);
      }

      if (isMountedRef.current) setIsRendered(true);
    }
    return () => {
      if (!isMountedRef.current) {
        tapForm.removeEventListener('submit', submitCard);
      }
    };
  }, [
    blueBirdScriptLoad,
    tapScriptLoad,
    isRendered,
    paymentData,
    languageDirection,
    currency,
    setIsSavingCard,
    setPaymentCard,
    tapPaymentDidLoad,
    setTapPaymentDidLoad,
    theme,
    t,
  ]);

  return (
    <StyledTapWrapper>
      <form ref={tapFormContainer} id="tap-form-container" method="post" action="/charge">
        <div id="tap-card-container" />
        {errorMessage && (
          <StyledErrorWrapper id="error-handler" role="alert">
            {errorMessage}
          </StyledErrorWrapper>
        )}
      </form>
    </StyledTapWrapper>
  );
};

export default TapPaymentCard;
